/* ThreadRowMenu — the per-thread "⋯" actions bubble.

   Replaces the old row of inline glyph buttons (⊕ rename / ✎ / 🗑) and the
   cramped in-row "Add to project" picker with a single three-dots button
   that opens an iOS-style popover (same spring/feel as the composer's "+"
   menu). Root actions: Rename, Add to project (drills into a project
   picker), Store takeaways (delete + extract memories), Delete permanently.

   The bubble is PORTALED to document.body with fixed positioning so it
   escapes the sidebar's scroll-container clip (overflow-y: auto would
   otherwise cut it off). The parent owns the real state machine (rename
   input, optimistic list updates, network calls); this component owns only
   its open/drill-in state. Projects are lazy-loaded by the parent the first
   time the user opens the Add-to-project view (onOpenAssign). */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isCoarsePointer } from '@/lib/useIsMobile';

export interface AssignProject {
  id: number;
  name: string;
}

type View = 'root' | 'assign';

export function ThreadRowMenu({
  onRename,
  onDelete,
  projects = null,
  onOpenAssign,
  onAssign,
  showAssign = true,
  busy = false,
}: {
  onRename: () => void;
  onDelete: (saveMemory: boolean) => void;
  /** Lazy-loaded by the parent; null = not loaded yet. */
  projects?: AssignProject[] | null;
  onOpenAssign?: () => void;
  onAssign?: (projectId: number) => void;
  /** Hide the "Add to project" action — e.g. inside a project's own
   *  Threads tab, where the thread is already anchored. Default true. */
  showAssign?: boolean;
  busy?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('root');
  // Add-to-project search filter.
  const [query, setQuery] = useState('');
  // Fixed (viewport) coords for the portaled bubble. Pops out to the RIGHT
  // of the dots button (into the chat area) so it never overlaps the thread
  // list, and escapes the sidebar's overflow clip.
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  const reposition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const W = 240, H = 264, M = 8;
    // Right of the button by default; flip to the left only if it would
    // overflow the viewport.
    let left = r.right + M;
    if (left + W > window.innerWidth - M) left = Math.max(M, r.left - W - M);
    // Anchor near the row top, clamped so the whole bubble stays on-screen.
    let top = r.top - 4;
    if (top + H > window.innerHeight - M) top = Math.max(M, window.innerHeight - H - M);
    if (top < M) top = M;
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Close on outside click (button + portaled bubble), on Escape, and on
  // scroll/resize (the anchored position would otherwise drift).
  useEffect(() => {
    if (!open) return;
    function onDoc(e: Event) {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || bubbleRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScroll(e: Event) {
      // Scrolling INSIDE the bubble (the project list) must not close it —
      // only an outside scroll (the sidebar list / window) detaches the
      // anchored bubble and should dismiss it.
      if (bubbleRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onResize() {
      // On phones the on-screen keyboard fires resize the instant the
      // search input focuses — re-clamp the bubble instead of closing.
      if (isCoarsePointer()) {
        reposition();
        return;
      }
      setOpen(false);
    }
    // pointerdown covers touch (iOS synthesizes mousedown late or not at
    // all); mousedown stays as the fallback where PointerEvent is absent.
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, reposition]);

  // Snap back to root after close so the next open starts at the top.
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => { setView('root'); setQuery(''); }, 200);
    return () => clearTimeout(t);
  }, [open]);

  function close() {
    setOpen(false);
  }

  return (
    <div className="trm">
      <button
        ref={btnRef}
        type="button"
        className={`trm-dots ${open ? 'on' : ''}`}
        aria-label="Thread actions"
        aria-haspopup="true"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <circle cx="3" cy="8" r="1.4" />
          <circle cx="8" cy="8" r="1.4" />
          <circle cx="13" cy="8" r="1.4" />
        </svg>
      </button>

      {createPortal(
        <div
          ref={bubbleRef}
          className={`trm-bubble ${open ? 'open' : ''}`}
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="trm-view" key={view}>
            {view === 'root' && (
              <>
                <div className="trm-group">
                  <button type="button" className="trm-row" onClick={() => { close(); onRename(); }}>
                    <span className="trm-row-ic"><IconPencil /></span>
                    <span className="trm-row-label">Rename</span>
                  </button>
                  {showAssign && onAssign && (
                    <button
                      type="button"
                      className="trm-row"
                      onClick={() => { setView('assign'); onOpenAssign?.(); }}
                    >
                      <span className="trm-row-ic"><IconFolder /></span>
                      <span className="trm-row-label">Add to project</span>
                      <span className="trm-row-chev"><IconChevron /></span>
                    </button>
                  )}
                </div>

                <div className="trm-sep" />

                <div className="trm-group">
                  <button
                    type="button"
                    className="trm-row"
                    disabled={busy}
                    onClick={() => { close(); onDelete(true); }}
                    title="Extract durable takeaways into memory, then delete the thread."
                  >
                    <span className="trm-row-ic"><IconBookmark /></span>
                    <span className="trm-row-label">Store takeaways &amp; delete</span>
                  </button>
                  <button
                    type="button"
                    className="trm-row danger"
                    disabled={busy}
                    onClick={() => { close(); onDelete(false); }}
                  >
                    <span className="trm-row-ic"><IconTrash /></span>
                    <span className="trm-row-label">Delete permanently</span>
                  </button>
                </div>
              </>
            )}

            {view === 'assign' && (() => {
              const q = query.trim().toLowerCase();
              const filtered = projects
                ? (q ? projects.filter((p) => p.name.toLowerCase().includes(q)) : projects)
                : null;
              return (
                <>
                  <div className="trm-drillhead">
                    <button type="button" className="trm-back" aria-label="Back" onClick={() => { setView('root'); setQuery(''); }}>
                      <IconBack />
                    </button>
                    <span className="trm-drillhead-title">Add to project</span>
                  </div>
                  {projects !== null && projects.length > 0 && (
                    <input
                      // Auto-popping the keyboard on touch shoves the
                      // anchored bubble around; let the user opt in.
                      autoFocus={!isCoarsePointer()}
                      className="trm-search"
                      type="text"
                      placeholder="Search projects…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  )}
                  <div className="trm-list">
                    {projects === null ? (
                      <div className="trm-empty">loading projects…</div>
                    ) : projects.length === 0 ? (
                      <div className="trm-empty">no projects available</div>
                    ) : filtered && filtered.length === 0 ? (
                      <div className="trm-empty">no projects match</div>
                    ) : (
                      filtered!.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="trm-opt"
                          title={p.name}
                          disabled={busy}
                          onClick={() => { close(); onAssign?.(p.id); }}
                        >
                          <span className="trm-opt-name">{p.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .trm { position: relative; display: flex; align-items: center; flex-shrink: 0; }
        .trm-dots {
          display: flex; align-items: center; justify-content: center;
          width: 24px; height: 24px; border: 0; border-radius: 6px;
          background: transparent; color: var(--ink-3); cursor: pointer;
          /* Subtle but always present so the affordance works in any row
             container (sidebar, project Threads tab); brightens on
             row-hover and when open. */
          opacity: 0.5; transition: background 120ms ease, color 120ms ease, opacity 120ms ease;
        }
        .sb-recent-row:hover .trm-dots, .pt-row:hover .trm-dots, .trm-dots.on { opacity: 1; }
        .trm-dots:hover { background: var(--bg-3); color: var(--ink); }
        .trm-dots.on { background: var(--accent); color: #fff; }
        /* Touch: no hover to reveal the dots, and 24px is below the 40px
           floor — full opacity always; the hit area grows via an overlay
           so the row layout doesn't shift. */
        @media (pointer: coarse) {
          .trm-dots { opacity: 1; position: relative; }
          .trm-dots::before { content: ''; position: absolute; inset: -8px; }
        }

        /* Portaled to body → fixed to the viewport, positioned via inline
           top/right. Escapes the sidebar's overflow clip. */
        .trm-bubble {
          position: fixed; z-index: 1000; width: 232px;
          background: var(--bg-2); border: 1px solid var(--rule-soft);
          border-radius: 14px;
          box-shadow: 0 16px 40px rgba(0,0,0,0.24), 0 2px 6px rgba(0,0,0,0.08);
          padding: 6px;
          transform-origin: top left;
          opacity: 0; visibility: hidden; pointer-events: none;
          transform: translateX(-6px) scale(.96);
          transition: opacity 140ms ease, transform 220ms cubic-bezier(.34,1.56,.64,1), visibility 0s linear 220ms;
        }
        .trm-bubble.open {
          opacity: 1; visibility: visible; pointer-events: auto;
          transform: translateX(0) scale(1);
          transition: opacity 120ms ease, transform 260ms cubic-bezier(.34,1.56,.64,1), visibility 0s;
        }
        .trm-view { animation: trmIn 180ms cubic-bezier(.2,.7,.3,1); }
        @keyframes trmIn { from { opacity: 0; transform: translateX(6px); } to { opacity: 1; transform: none; } }

        .trm-group { display: flex; flex-direction: column; }
        .trm-sep { height: 1px; background: var(--rule-hair); margin: 5px 6px; }
        .trm-row {
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 9px 10px;
          background: transparent; border: 0; border-radius: 9px;
          font: inherit; font-size: 13px; color: var(--ink);
          text-align: left; cursor: pointer;
          transition: background 110ms ease;
        }
        .trm-row:hover { background: var(--bg-1); }
        .trm-row:disabled { opacity: .45; cursor: default; }
        .trm-row:disabled:hover { background: transparent; }
        .trm-row-ic { display: flex; color: var(--accent); flex-shrink: 0; }
        .trm-row-label { flex: 1; min-width: 0; }
        .trm-row-chev { display: flex; color: var(--ink-3); flex-shrink: 0; }
        .trm-row.danger { color: var(--danger); }
        .trm-row.danger .trm-row-ic { color: var(--danger); }
        .trm-row.danger:hover { background: color-mix(in srgb, var(--danger) 12%, transparent); }

        .trm-drillhead {
          display: flex; align-items: center; gap: 8px;
          padding: 3px 4px 7px; margin-bottom: 2px;
          border-bottom: 1px solid var(--rule-hair);
        }
        .trm-back {
          display: flex; align-items: center; justify-content: center;
          width: 24px; height: 24px; border: 0; border-radius: 7px;
          background: transparent; color: var(--ink-2); cursor: pointer;
        }
        .trm-back:hover { background: var(--bg-1); color: var(--ink); }
        .trm-drillhead-title {
          font-family: 'JetBrains Mono', monospace; font-size: 10px;
          letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-3);
        }
        .trm-search {
          width: 100%; box-sizing: border-box; margin: 6px 0 2px;
          background: var(--bg-1); color: var(--ink);
          border: 1px solid var(--rule-hair); border-radius: 8px;
          padding: 7px 10px; font: inherit; font-size: 12px; outline: 0;
        }
        .trm-search:focus { border-color: var(--accent); }
        .trm-list { display: flex; flex-direction: column; gap: 2px; max-height: 200px; overflow-y: auto; padding-top: 4px; }
        .trm-empty { font-size: 12px; color: var(--ink-3); padding: 8px 6px; text-align: center; }
        .trm-opt {
          display: flex; align-items: center; width: 100%;
          padding: 8px 10px; border-radius: 8px;
          background: transparent; border: 1px solid transparent;
          font: inherit; font-size: 13px; color: var(--ink-2);
          text-align: left; cursor: pointer;
          transition: background 110ms ease, color 110ms ease;
        }
        .trm-opt:hover { background: var(--bg-1); color: var(--accent); }
        .trm-opt:disabled { opacity: .5; cursor: default; }
        /* Long project names (the identifying digits often sit deep in the
           name) stay one line and scroll horizontally within the row so the
           whole name is readable without truncation. */
        .trm-opt-name {
          min-width: 0; flex: 1;
          white-space: nowrap; overflow-x: auto; overflow-y: hidden;
          scrollbar-width: thin; -ms-overflow-style: none;
          overscroll-behavior-x: contain;
        }
        .trm-opt-name::-webkit-scrollbar { height: 4px; }
        .trm-opt-name::-webkit-scrollbar-thumb { background: var(--rule-soft); border-radius: 2px; }
        .trm-opt-name::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>
  );
}

/* ---------------- icons (line, inherit color) ---------------- */
function IconPencil()  { return <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M11.5 2.5l2 2L6 12l-2.5.5L4 10z" /></svg>; }
function IconFolder()  { return <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M2 4h4l1.3 1.5H14V13H2z" /></svg>; }
function IconBookmark(){ return <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M4 2.5h8V14l-4-2.5L4 14z" /></svg>; }
function IconTrash()   { return <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M3 4.5h10M6.5 4.5V3h3v1.5M5 4.5l.5 8h5l.5-8" /></svg>; }
function IconChevron() { return <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M6 3l5 5-5 5" /></svg>; }
function IconBack()    { return <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M10 3L5 8l5 5" /></svg>; }
